import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  AutomationStatus,
  ExperienceStatus,
  IAutomation,
  IAutomationTemplate,
  IExperience,
  IExperienceTemplate,
  ISupportingActor,
  ITask,
  ITaskTemplate,
  PARTICIPANT_ACTOR_KEY,
  TaskStatus
} from '@dadohr/shared/domainTypes/experience';
import { IUser } from '@dadohr/shared/domainTypes/user';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { Button, IconButton, Tooltip, Typography } from '@material-ui/core';
import { compact, keyBy, mapValues, omit } from 'lodash';
import { RefreshCw, User } from 'lucide-react';
import React, { useMemo, useRef, useState } from 'react';
import { CustomInputLabel } from '../../../../components/CustomInputLabel';
import { InfoBox } from '../../../../components/InfoBox';
import { FixedPopover } from '../../../../components/Popover';
import { DateTimeField } from '../../../../components/Time/DateTimeField';
import {
  Timeline,
  TimelineAnnotation,
  TimelineItem
} from '../../../../components/Timeline';
import { useHover } from '../../../../hooks/useHover';
import { useUnsafeTranslation } from '../../../../i18next';
import { joinWithCommaAndAnd } from '../../../../services/enumerate';
import {
  getParticipantActor,
  getUserActorForKey,
  moveSelectedExperienceItemsRelatively,
  repositionAnchorForInstance,
  updateActorForInstance,
  useActorsForExperience
} from '../../../../services/experiences/instance';
import { InstanceRecalculateReason } from '../../../../services/experiences/recalcualateInstance';
import {
  getAllMessageTemplates,
  getMessageBatchesForTemplate
} from '../../../../services/experiences/template';
import { DEFAULT_MESSAGE_TIMELINE_LENGTH_IN_DAYS } from '../../../../services/experiences/timing';
import { LightMdToJsx } from '../../../../services/formatter';
import {
  addToTs,
  fromMoment,
  now,
  ONE_DAY,
  Timestamp,
  toMoment,
  tsIsAfter
} from '../../../../services/time';
import { getUser, toFullName } from '../../../../services/user';
import {
  ActorDescriptor,
  ActorDescriptorExtended,
  ActorLegend,
  ActorLegendEditable,
  useActorDescriptorsFromTemplate
} from '../../../Experiences/components/Actors';
import { getItemDisplayName } from '../../../Experiences/helpers/adminName';
import { useCurrentUser } from '../../../../services/currentUser';
import { interpolateExperienceText } from '../../../../services/set/interpolation';
import {
  AnchorField,
  useAllAbsoluteAnchorRulersWithDates,
  useAnchorWarning
} from '../../../Experiences/components/Anchors';
import { InstanceStatusChip } from '../../../Experiences/components/InstanceStatusChip';
import { MessageItem2 } from '../../../Experiences/components/Messages';
import {
  TaskTimelineZoomControls,
  TaskTimelineZoomControlsContainer,
  useTaskTimelineScaleChangeRequest
} from '../../../Experiences/components/TaskTimelineZoomControls';
import {
  AutomationTimelineItem,
  getTimelineAutomationColor,
  getTimelineTaskColor,
  sortTaskTimelineItems,
  TaskTimelineItem
} from '../../../Experiences/pages/Details/TaskTimeline';
import { AutomationPopoverBody, TaskPopoverBody } from './PopoverBody';

type Variant = 'dense' | 'normal';

const InfoBoxContainer = styled('div')((p) => ({
  marginBottom: p.theme.spacing(4)
}));

const TaskTimelineItemForUser = ({
  tpl,
  variant,
  actor,
  task,
  selected,
  onSelect,
  onClick,
  connectUp,
  connectDown,
  expTitle,
  orgName
}: {
  tpl: ITaskTemplate;
  variant?: Variant;
  actor: ActorDescriptor | undefined;
  task: ITask;
  selected?: boolean;
  onSelect?: (nextSelected: boolean) => void;
  onClick?: () => void;
  connectUp?: boolean;
  connectDown?: boolean;
  expTitle: string;
  orgName: string;
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const hovered = useHover(ref);
  const theme = useTheme();
  const colors = getTimelineTaskColor(theme.custom.colors, task.status)[
    variant === 'dense' ? 'normal' : 'light'
  ];
  const displayTitle = interpolateExperienceText(
    getItemDisplayName(tpl),
    {
      experience: { vars: { title: expTitle, tasks: {} } },
      org: { vars: { name: orgName } },
      roles: []
    },
    { dateFormat: 'YYYY/MM/DD' }
  );

  return (
    <>
      <TaskTimelineItem
        ref={ref}
        title={displayTitle}
        channels={tpl.channels}
        actor={actor}
        selected={selected}
        onSelect={onSelect}
        onClick={onClick}
        variant={variant}
        fontColor={colors.text}
        bgColor={colors.bg}
        customizedTiming={
          task.timing.start.toMillis() > Date.now() &&
          task.timing.manualOverride
        }
        transparent={task.status === TaskStatus.SKIPPED}
        connectUp={connectUp}
        connectDown={connectDown}
      />
      {variant === 'normal' && (
        <FixedPopover open={hovered} anchor={ref.current}>
          <TaskPopoverBody task={task} tpl={tpl} expTitle={expTitle} orgName={orgName} />
        </FixedPopover>
      )}
    </>
  );
};

const AutomationTimelineItemForUser = ({
  tpl,
  variant,
  automation,
  selected,
  onSelect,
  onClick
}: {
  tpl: IAutomationTemplate;
  variant?: Variant;
  automation: IAutomation;
  selected?: boolean;
  onSelect?: (nextSelected: boolean) => void;
  onClick?: () => void;
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const hovered = useHover(ref);
  const theme = useTheme();
  const colors = getTimelineAutomationColor(
    theme.custom.colors,
    automation.status
  )[variant === 'dense' ? 'normal' : 'light'];

  return (
    <>
      <AutomationTimelineItem
        ref={ref}
        tpl={tpl}
        fontColor={colors.text}
        bgColor={colors.bg}
        transparent={automation.status === AutomationStatus.SKIPPED}
        variant={variant}
        selected={selected}
        onSelect={onSelect}
        onClick={onClick}
        customizedTiming={
          automation.timing.at.toMillis() > Date.now() &&
          automation.timing.manualOverride
        }
      />
      {variant === 'normal' && (
        <FixedPopover open={hovered} anchor={ref.current}>
          <AutomationPopoverBody tpl={tpl} automation={automation} />
        </FixedPopover>
      )}
    </>
  );
};

const UserAnnotation = () => {
  return (
    <Button variant="outlined" size="small" startIcon={<User size={16} />}>
      1
    </Button>
  );
};

const EditRow = styled('div')((p) => ({
  display: 'grid',
  gridGap: p.theme.spacing(2),
  gridTemplateColumns: 'repeat(5, minmax(200px, 230px))',
  gridTemplateRows: 'auto',
  marginBottom: p.theme.spacing(4)
}));

export const applyAnchorDateToExperience = async (
  tpl: Doc<IExperienceTemplate>,
  d: Doc<IExperience>,
  nextAnchorDate: Timestamp,
  opts: {
    overrideStartsAtDate?: boolean;
    honorCatchupModeSettings?: boolean;
  } = {}
): Promise<Doc<IExperience> | null> => {
  const participantactor = getParticipantActor(d);
  if (!participantactor) {
    console.error('PARTICIPANT CACTOR NOT FOUND', d);
    return null;
  }
  const participant = await getUser(participantactor.userId);
  if (!participant) {
    console.error('PARTICIPANT USER NOT FOUND', d);
    return null;
  }

  const n = now();
  const startsInTheFuture = tsIsAfter(d.data.startsAt, n);
  return repositionAnchorForInstance(
    d,
    tpl,
    participant,
    nextAnchorDate,
    // We're using the start date of the current experience as the reference point, so that all timeline items
    // can move appropiately. If we would choose a now() date we would run into problems with experiences
    // that are in catch up mode, as several items would be deemed as being in the past.
    // This only applies to instances which should also move their start date around (e.g. being in enroll mode)
    startsInTheFuture ? n : opts.overrideStartsAtDate ? d.data.startsAt : n,
    InstanceRecalculateReason.INSTANCE_CHANGE,
    {
      overrideStartsAtDate: opts.overrideStartsAtDate || startsInTheFuture,
      honorCatchupModeSettings: !!opts.honorCatchupModeSettings
    }
  );
};

const calibrateTimingsToAlignOnADayBasedGrid = (
  startsAt: Timestamp,
  tz: string,
  items: TimelineItem[]
): TimelineItem[] => {
  // Nivellates any particular start time for all items.
  // Everything is aligned to the starting hour and minute of the startsAt date
  // This way an item e.g. on Tue 6am is guarenteed to be showed on the same day
  // as an iem on Tue 8am
  const startsAtM = toMoment(startsAt).tz(tz);
  return items.map((item) => {
    const diff = item.end - item.start;
    const nextStartAbs = toMoment(addToTs(startsAt, item.start))
      .tz(tz)
      .hour(startsAtM.hour())
      .minute(startsAtM.minute());

    const nextStart = nextStartAbs.valueOf() - startsAt.toMillis();
    return {
      ...item,
      start: nextStart,
      end: nextStart + diff
    };
  });
};

export const UserTaskTimeline = ({
  d,
  tpl,
  variant,
  onChange,
  anchorFields,
  selections,
  setSelections,
  hasZoomControls,
  missingSupportingActors,
  isInEnrollMode = false,
  showActorNames = false,
  showActorsAndAnchorRow = true,
  disableItemDrag = false
}: {
  d: Doc<IExperience>;
  tpl: Doc<IExperienceTemplate>;
  variant: Variant;
  onChange?: (nextD: Doc<IExperience>) => void;
  anchorFields: AnchorField[];
  hasZoomControls?: boolean;
  /**
   * When true, timeline items can no longer be dragged to reschedule (the
   * canvas becomes a read-only viewer). Selection, zoom and hover popovers are
   * unaffected. The new-UX participant dialog sets this because rescheduling
   * there is done through the items table / details panel instead.
   */
  disableItemDrag?: boolean;
  /**
   * ParticipantDialog's new-UX path renders actor assignment/anchor-date editing in its
   * own standalone "Supporting actors and dates" section instead, so it passes `false`
   * here to avoid showing this row twice. All other call sites keep the default.
   */
  showActorsAndAnchorRow?: boolean;

  selections?: {
    tasks: { [taskTplId: string]: boolean };
    messages: { [msgTplId: string]: boolean };
    automations: { [aTplId: string]: boolean };
  };
  setSelections?: React.Dispatch<
    React.SetStateAction<{
      tasks: { [taskTplId: string]: boolean };
      messages: { [msgTplId: string]: boolean };
      automations: { [aTplId: string]: boolean };
    }>
  >;
  showActorNames?: boolean;
  missingSupportingActors?: ISupportingActor[];
  isInEnrollMode?: boolean;
}) => {
  const editable = !!onChange;
  const { t } = useUnsafeTranslation();
  const { org } = useCurrentUser();
  const { anchor } = tpl.data;
  const { automations, tasks, messagesStatuses } = d.data;

  const actorDescriptors = useActorDescriptorsFromTemplate(
    tpl.data.supportingActors
  );

  const [allActors] = useActorsForExperience(d);
  const actorDescriptorsForLegend = useMemo(() => {
    const descriptorsWithoutParticipant = omit(actorDescriptors, [
      PARTICIPANT_ACTOR_KEY
    ]);
    if (!allActors || !showActorNames) {
      return descriptorsWithoutParticipant;
    }
    return mapValues(descriptorsWithoutParticipant, (v) => {
      return {
        ...v,
        label: allActors[v.key]
          ? `${v.label}: ${toFullName(allActors[v.key].data)}`
          : v.label
      };
    });
  }, [actorDescriptors, allActors, showActorNames]);

  const actorDescriptorsExtendedForLegend = useMemo(() => {
    return mapValues(
      actorDescriptorsForLegend,
      (v): ActorDescriptorExtended => {
        return {
          ...v,
          user: (allActors || {})[v.key] || null
        };
      }
    );
  }, [actorDescriptorsForLegend, allActors]);

  const changeHandlers = useMemo(() => {
    if (!onChange) {
      return {
        onMove: undefined
      };
    }
    return {
      onMove: (
        key: string,
        positions: {
          next: { start: number; end: number };
          prev: { start: number; end: number };
          diff: { start: number; end: number };
        },
        data: { type: 'TASK' | 'AUTOMATION' | 'MESSAGE' }
      ) => {
        // If nothing is selected, we pretend to have a selection: The item that has been dragged
        // If a selection is present we check if the dragged element is one of the selected ones.
        //   - if yes: move all selected items
        //   - if no: move the dragged item (alternatively we could choose to do nothing)

        if (selections) {
          if (
            (data.type === 'TASK' && selections.tasks[key]) ||
            (data.type === 'MESSAGE' && selections.messages[key]) ||
            (data.type === 'AUTOMATION' && selections.automations[key])
          ) {
            onChange(
              moveSelectedExperienceItemsRelatively(
                tpl,
                d,
                selections,
                positions.diff.start,
                now()
              )
            );
            return;
          }
        }

        const pretendedSelections: {
          tasks: { [taskId: string]: boolean };
          automations: { [aId: string]: boolean };
          messages: { [msgId: string]: boolean };
        } = { tasks: {}, automations: {}, messages: {} };
        if (data.type === 'TASK') {
          pretendedSelections.tasks[key] = true;
        }
        if (data.type === 'MESSAGE') {
          pretendedSelections.messages[key] = true;
        }
        if (data.type === 'AUTOMATION') {
          pretendedSelections.automations[key] = true;
        }

        onChange(
          moveSelectedExperienceItemsRelatively(
            tpl,
            d,
            pretendedSelections,
            positions.diff.start,
            now()
          )
        );
      }
    };
  }, [d, tpl, onChange, selections]);

  const { startsAt, anchorDate } = d.data;

  const items = useMemo(() => {
    // When dragging is disabled (new-UX viewer), the whole item card becomes a
    // click target that toggles selection — not just the left icon/checkbox.
    // Classic (draggable) items keep the icon-only checkbox so a card click
    // stays free for the drag gesture.
    const makeBodyClick =
      disableItemDrag && setSelections
        ? (kind: 'tasks' | 'messages' | 'automations', id: string) => () =>
            setSelections((x) => ({
              ...x,
              [kind]: { ...x[kind], [id]: !x[kind][id] }
            }))
        : undefined;

    const tasksByTplId = keyBy(tasks, (task) => task.templateId);
    const allMessages = getAllMessageTemplates(tpl.data.messages).filter(
      (m) => !!messagesStatuses[m.id]
    );

    const messageBatches = getMessageBatchesForTemplate(tpl);
    // after we have the batches, we need to check if the respective items
    // on the instance are also sitting together. If we, we draw the batch connections.
    // It's e.g. possible that some of them got moved and are therefore not part of
    // a batch.
    const connectUpIds = new Set<string>();
    const connectDownIds = new Set<string>();
    messageBatches.forEach((batch) => {
      const msgStatus = messagesStatuses[batch.message.id];
      if (!msgStatus) {
        return;
      }
      const at = msgStatus.timing.at;
      const batchedTasks = batch.tasks.filter((t) => {
        const task = tasksByTplId[t.id];
        return task && task.timing.start.isEqual(at);
      });
      if (batchedTasks.length) {
        connectDownIds.add(batch.message.id);
        batchedTasks.forEach((t) => connectUpIds.add(t.id));
        batchedTasks.slice(0, -1).forEach((t) => connectDownIds.add(t.id));
      }
    });

    return sortTaskTimelineItems(
      calibrateTimingsToAlignOnADayBasedGrid(
        startsAt,
        tpl.data.tz,
        compact([
          ...allMessages.map<TimelineItem | null>((m) => {
            const messageData = messagesStatuses[m.id];
            const timing = messageData?.timing;
            if (!timing) {
              console.log('NO TIMING FOR MESSAGE', m);
              return null;
            }
            const sendAt = timing.at.toMillis() - startsAt.toMillis();
            return {
              key: m.id,
              start: sendAt,
              end: sendAt + ONE_DAY * DEFAULT_MESSAGE_TIMELINE_LENGTH_IN_DAYS,
              data: { type: 'MESSAGE', actor: m.actor, batchMode: m.batchMode },
              body: (
                <MessageItem2
                  tplContext={tpl.data}
                  variant={variant}
                  message={m}
                  actor={actorDescriptors[m.actor]}
                  selected={selections?.messages[m.id]}
                  onClick={makeBodyClick?.('messages', m.id)}
                  onSelect={
                    setSelections &&
                    ((nextSelected) =>
                      setSelections((x) => ({
                        ...x,
                        messages: {
                          ...x.messages,
                          [m.id]: nextSelected
                        }
                      })))
                  }
                  customizedTiming={
                    timing.at.toMillis() > Date.now() && timing.manualOverride
                  }
                  status={messageData?.status || null}
                  connectDown={connectDownIds.has(m.id)}
                />
              ),
              movable: editable && !disableItemDrag
            };
          }),
          ...tpl.data.tasks.map<TimelineItem | null>((taskTpl) => {
            const task = tasksByTplId[taskTpl.id];
            if (!task) {
              console.error(
                'TASK_NOT_FOUND',
                tasks,
                tpl.data.tasks,
                taskTpl.id
              );
              return null;
            }
            const { timing } = task;
            if (!timing) {
              console.log('NO TIMING FOR TASK', task);
              return null;
            }
            const start = timing.start.toMillis() - startsAt.toMillis();
            let end = timing.end.toMillis() - startsAt.toMillis();
            if (end < start) {
              end = start + ONE_DAY;
            }
            return {
              key: taskTpl.id,
              start,
              end,
              data: {
                type: 'TASK',
                actor: taskTpl.actor,
                batchMode: taskTpl.batchMode
              },
              body: (
                <TaskTimelineItemForUser
                  variant={variant}
                  tpl={taskTpl}
                  task={task}
                  actor={actorDescriptors[taskTpl.actor]}
                  selected={selections?.tasks[taskTpl.id]}
                  onClick={makeBodyClick?.('tasks', taskTpl.id)}
                  onSelect={
                    setSelections &&
                    ((nextSelected) =>
                      setSelections((x) => ({
                        ...x,
                        tasks: {
                          ...x.tasks,
                          [taskTpl.id]: nextSelected
                        }
                      })))
                  }
                  connectUp={connectUpIds.has(taskTpl.id)}
                  connectDown={connectDownIds.has(taskTpl.id)}
                  expTitle={tpl.data.title}
                  orgName={org.name}
                />
              ),
              resizable: false,
              movable: editable && !disableItemDrag
            };
          }),

          ...tpl.data.automations.map<TimelineItem | null>((aTpl) => {
            const automation = automations[aTpl.id];
            if (!automation) {
              console.error(
                'AUTOMATION_NOT_FOUND',
                automations,
                tpl.data.automations,
                aTpl.id
              );
              return null;
            }

            const { timing } = automation;
            if (!timing) {
              console.log('AUTOMATION TIMING NOT FOUND', automation);
              return null;
            }

            const relativeStart = timing.at.toMillis() - startsAt.toMillis();

            return {
              key: aTpl.id,
              start: relativeStart,
              end: relativeStart + ONE_DAY * 5,
              data: { type: 'AUTOMATION' },
              body: (
                <AutomationTimelineItemForUser
                  tpl={aTpl}
                  variant={variant}
                  automation={automation}
                  selected={selections?.automations[automation.templateId]}
                  onClick={makeBodyClick?.('automations', automation.templateId)}
                  onSelect={
                    setSelections &&
                    ((nextSelected) =>
                      setSelections((x) => ({
                        ...x,
                        automations: {
                          ...x.automations,
                          [automation.templateId]: nextSelected
                        }
                      })))
                  }
                />
              ),
              movable: editable && !disableItemDrag
            };
          })
        ])
      )
    );
  }, [
    tpl,
    variant,
    automations,
    tasks,
    messagesStatuses,
    actorDescriptors,
    startsAt,
    editable,
    disableItemDrag,
    selections,
    setSelections,
    org
  ]);

  const expectedEnd =
    d.data.expectedEnd.toMillis() -
    d.data.startsAt.toMillis() +
    tpl.data.expectedEndThreshold;

  const anchorRulers = useAllAbsoluteAnchorRulersWithDates(
    startsAt,
    anchor,
    anchorDate,
    anchorFields,
    t,
    expectedEnd,
    items.length > 0
  );

  // This only works with a SINGLE ruler that needs to be manipulated.
  // We currently have no case of several - so this is fine, but we'd need
  // to revisit this if we ever change this
  const anchorThatCanBeManipulated =
    anchorRulers.find((a) => !a.static) ||
    (isInEnrollMode && anchorRulers.find((a) => a.position === 0)) ||
    null;

  const [anchorWarning] = useAnchorWarning(
    getParticipantActor(d)?.userId || '',
    anchor,
    anchorDate,
    tpl.data.tz
  );

  const annotations = useMemo<TimelineAnnotation[] | undefined>(() => {
    if (
      d.data.status === ExperienceStatus.RUNNING ||
      d.data.status === ExperienceStatus.LATE
    ) {
      const start = Date.now() - d.data.startsAt.toMillis();
      return [
        {
          key: 'user',
          start,
          end: start,
          body: <UserAnnotation />,
          center: true
        }
      ];
    }
    return undefined;
  }, [d]);

  const [scale, setScale] = useState(1);
  const onScaleChangeRequest = useTaskTimelineScaleChangeRequest(setScale);

  const innerUnitSpan = ONE_DAY * 1;
  const outerUnitSpan = ONE_DAY * 7;

  return (
    <>
      {showActorsAndAnchorRow && (
      <EditRow>
        {anchorThatCanBeManipulated && onChange && (
          <CustomInputLabel
            label={anchorThatCanBeManipulated.shortLabel}
            warning={
              anchorWarning
                ? t(
                    `This date applies to this experience only. It differs from the user's profile ({{ date }}).`,
                    {
                      date: toMoment(anchorWarning.original)
                        .tz(tpl.data.tz)
                        .format('YYYY/MM/DD')
                    }
                  )
                : ''
            }
            otherElements={
              anchorWarning ? (
                <Tooltip
                  title={`${t(
                    'Click to reset to employee profile date ({{ date }})',
                    {
                      date: toMoment(anchorWarning.original)
                        .tz(tpl.data.tz)
                        .format('YYYY/MM/DD')
                    }
                  )}`}
                >
                  <IconButton
                    onClick={async () => {
                      if (!anchorWarning) {
                        return;
                      }

                      const currentM = toMoment(anchorWarning.current).tz(
                        tpl.data.tz
                      );
                      const nextDate = fromMoment(
                        toMoment(anchorWarning.original)
                          .tz(tpl.data.tz)
                          .hour(currentM.hour())
                          .minute(currentM.minute())
                      );

                      const nextExperience = await applyAnchorDateToExperience(
                        tpl,
                        d,
                        nextDate,
                        {
                          overrideStartsAtDate: isInEnrollMode,
                          honorCatchupModeSettings: isInEnrollMode
                        }
                      );
                      if (nextExperience) {
                        onChange(nextExperience);
                      }
                    }}
                  >
                    <RefreshCw size={14} />
                  </IconButton>
                </Tooltip>
              ) : null
            }
          >
            <DateTimeField
              fullWidth
              value={anchorDate.at}
              onChange={async (nextDate) => {
                // TODO check if next date differs from the normal anchor date
                // - if yes, ask the user for additional confirmation.
                const nextExperience = await applyAnchorDateToExperience(
                  tpl,
                  d,
                  nextDate,
                  {
                    overrideStartsAtDate: isInEnrollMode,
                    honorCatchupModeSettings: isInEnrollMode
                  }
                );
                if (nextExperience) {
                  onChange(nextExperience);
                }
              }}
              minutesStep={10}
              tz={tpl.data.tz}
            />
          </CustomInputLabel>
        )}
        {!!Object.keys(actorDescriptorsForLegend).length &&
          (onChange ? (
            <ActorLegendEditable
              orgId={d.data.orgId}
              descriptors={actorDescriptorsExtendedForLegend}
              onChange={async (key, nextActor) => {
                const participantactor = getUserActorForKey(
                  d,
                  PARTICIPANT_ACTOR_KEY
                );
                if (!participantactor) {
                  console.error('PARTICIPANT ACTOR NOT FOUND', d);
                  return;
                }
                const participant = await getUser(participantactor.userId);
                if (!participant) {
                  console.error('PARTICIPANT USER NOT FOUND', d);
                  return;
                }
                return onChange(
                  updateActorForInstance(tpl, d, participant, key, nextActor.id)
                );
              }}
            />
          ) : (
            <ActorLegend descriptors={actorDescriptorsForLegend} />
          ))}
      </EditRow>
      )}

      {!!missingSupportingActors?.length && (
        <InfoBoxContainer>
          <InfoBox>
            <LightMdToJsx>
              {t(
                '**Missing {{ actors }}.** You can start this experience without them, but it may cause issues later.',
                {
                  actors: joinWithCommaAndAnd(
                    missingSupportingActors.map((a) => a.name),
                    t
                  )
                }
              )}
            </LightMdToJsx>
          </InfoBox>
        </InfoBoxContainer>
      )}

      <Timeline
        items={items}
        innerUnitSpan={innerUnitSpan}
        outerUnitSpan={outerUnitSpan}
        renderOuterUnit={(i, gridOffset) => {
          let start = toMoment(addToTs(startsAt, i * outerUnitSpan))
            .tz(tpl.data.tz)
            .add(gridOffset, 'ms');
          const end = start
            .clone()
            .add(outerUnitSpan - innerUnitSpan, 'milliseconds');
          return `${start.format(
            i === 0 ? 'dddd \n MM/DD' : 'MM/DD'
          )} – ${end.format('MM/DD')}`;
        }}
        grid={variant === 'normal'}
        header={variant === 'normal'}
        scale={scale}
        onScaleChangeRequest={
          hasZoomControls ? onScaleChangeRequest : undefined
        }
        scrollIntoView={annotations ? annotations[0].start : undefined}
        annotations={annotations}
        rulers={variant === 'dense' ? undefined : anchorRulers}
        onMove={disableItemDrag ? undefined : changeHandlers.onMove}
      />

      {hasZoomControls && (
        <TaskTimelineZoomControlsContainer stickyOffset={32}>
          <TaskTimelineZoomControls value={scale} onChange={setScale} />
        </TaskTimelineZoomControlsContainer>
      )}
    </>
  );
};

const Heading = styled('div')((p) => ({
  fontSize: p.theme.custom.fontSize.s,
  color: p.theme.custom.colors.gray.w500,
  textTransform: 'uppercase',
  marginBottom: p.theme.spacing()
}));

const Title = styled('div')((p) => ({
  display: 'flex',
  alignItems: 'center',
  gap: p.theme.spacing(2)
}));

export const UserTaskTimelineHeading = ({
  d,
  tpl,
  user
}: {
  d: Doc<IExperience>;
  tpl: Doc<IExperienceTemplate>;
  user: Doc<IUser> | null;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <>
      <Heading>
        {user
          ? toFullName(user.data) || user.data.email || user.data.personalEmail
          : t('Unknown user')}
      </Heading>
      <Title>
        <Typography variant="h4">{tpl.data.title}</Typography>
        <InstanceStatusChip status={d.data.status} />
      </Title>
    </>
  );
};
